import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { ThemeColors } from 'styled-components';
import { rgba } from 'polished';
import { camelCase, sortBy, last } from 'lodash';
import moment from 'moment';

import { Button, Card, Paragraph, Subheading, Title, Callout, Spinner } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';
import { GiftIcon } from '@treams/treams-icons';
import { PropsWithChildren, ReactElement, ComponentProps } from 'react';
import { AccountSwitcher } from 'navigation/Menu';

const Container = styled(Box)<{ $modal?: boolean }>`
	${(props) => (props.$modal ? 'overflow-y: auto;' : 'height: 100%;')}
	background: ${(props) => props.theme.colors.whiteBlue};
`;

const planColors: { [K in graphql.WorkspacePlanType]: keyof ThemeColors } = {
	[graphql.WorkspacePlanType.Pro]: 'primary',
	[graphql.WorkspacePlanType.Conversations]: 'employee',
	[graphql.WorkspacePlanType.Engage]: 'manager',
	[graphql.WorkspacePlanType.Classic]: 'primaryLight',
};

const Product = styled(Card)<{ type: graphql.WorkspacePlanType }>`
	border-top: 8px solid ${(props) => props.theme.colors[planColors[props.type]]};
	border-radius: 8px;

	${(props) =>
		props.type === graphql.WorkspacePlanType.Pro &&
		`
        box-shadow:
            0px 0px 0px 1px ${props.theme.colors[planColors[props.type]]},
            0px 0px 0px 3px ${rgba(props.theme.colors[planColors[props.type]], 0.25)}
    `}
`;

const Sticky = styled(Box)`
	position: sticky;
	bottom: 0;
	background: ${(props) => props.theme.colors.whiteBlue};
	border-top: 1px solid ${(props) => props.theme.colors.lightBlue};
	padding: 16px 4px 16px 4px;
	margin: 16px -4px 0px -4px;
`;

const featureKeys = [
	'pulseSurveys',
	'MTO',
	'customQuestions',
	'360Feedback',
	'conversations',
	'skillsMatrix',
	'developmentPlans',
	'goals',
	'talentReviews',
	'learning',
	'analytics',
] as const;

const planFeatures: { [K in graphql.WorkspacePlanType]: (typeof featureKeys)[number][] } = {
	[graphql.WorkspacePlanType.Pro]: [
		'pulseSurveys',
		'MTO',
		'customQuestions',
		'360Feedback',
		'conversations',
		'skillsMatrix',
		'developmentPlans',
		'goals',
		'talentReviews',
		'learning',
		'analytics',
	],
	[graphql.WorkspacePlanType.Conversations]: ['360Feedback', 'conversations', 'goals', 'analytics'],
	[graphql.WorkspacePlanType.Engage]: ['pulseSurveys', 'MTO', 'customQuestions', 'analytics'],
	[graphql.WorkspacePlanType.Classic]: [],
};

type ProductPlanView = Pick<graphql.WorkspacePlanV2, 'isTrial' | 'isExpired' | 'endDate'>;

function ProductPlan(
	props: PropsWithChildren<{
		type: graphql.WorkspacePlanType;
		plans: ProductPlanView[];
	}>,
) {
	const { t } = useTranslation();
	const workspace = useWorkspace();

	const [create] = graphql.useCreateSubscriptionMutation({});

	const [cancel] = graphql.useCancelSubscriptionMutation({
		variables: { input: { workspaceID: workspace.id } },
	});

	async function subscribe(planType: graphql.WorkspacePlanType) {
		const response = await create({
			variables: {
				input: {
					workspaceID: workspace.id,
					interval: graphql.BillingInterval.Monthly,
					planType,
				},
			},
		});

		window.location.href = response.data!.createSubscription.link;
	}

	const translationKey = camelCase(props.type);

	const hasContinuousPlan = props.plans.some((plan) => !plan.endDate);
	const lastFinitePlan = last(
		sortBy(
			props.plans.filter((plan) => !!plan.endDate),
			(plan) => moment(plan.endDate).valueOf(),
		),
	);

	const planName = t(`workspaces.plans.type.${translationKey}`);

	return (
		<>
			<Product type={props.type}>
				<Subheading>Treams {planName}</Subheading>
				<Paragraph mb={3}>{t(`workspaces.plans.description.${translationKey}`)}</Paragraph>
				{planFeatures[props.type].map((feature, idx) => (
					<Paragraph key={idx}>
						✅&nbsp; {t(`workspaces.plans.featureDescription.${feature}`)}
					</Paragraph>
				))}
				{!hasContinuousPlan &&
					lastFinitePlan &&
					(lastFinitePlan.isExpired ? (
						<Callout color="danger" mt={3}>
							{t(
								`workspaces.plans.expiry.${lastFinitePlan.isTrial ? 'trial' : 'subscription'}.past`,
								{
									type: planName,
								},
							)}
						</Callout>
					) : (
						<Callout color="yellow" mt={3}>
							{t(
								`workspaces.plans.expiry.${
									lastFinitePlan.isTrial ? 'trial' : 'subscription'
								}.future`,
								{
									type: planName,
									count: moment(lastFinitePlan.endDate).diff(moment(), 'days'),
								},
							)}
						</Callout>
					))}
			</Product>
			<Sticky>
				{props.plans.length === 0 || props.plans.every((plan) => plan.isTrial || plan.isExpired) ? (
					<Button
						icon={<GiftIcon />}
						title={t('workspaces.plans.subscribe')}
						onClick={async () => {
							await subscribe(props.type);
						}}
					/>
				) : (
					<Button
						type="secondary"
						title={t('workspaces.plans.manage')}
						onClick={async () => {
							const response = await cancel({
								variables: {
									input: {
										workspaceID: workspace.id,
									},
								},
							});

							window.location.href = response.data!.cancelSubscription.link;
						}}
					/>
				)}
			</Sticky>
		</>
	);
}

export default function Billing(props: { modal?: boolean }) {
	const query = graphql.useViewerWorkspacePlanQuery();

	const { t } = useTranslation('', { keyPrefix: 'settings.billing' });

	const planCards = Object.fromEntries(
		Object.keys(graphql.WorkspacePlanType).map(
			(planTypeKey: keyof typeof graphql.WorkspacePlanType) => {
				const planType = graphql.WorkspacePlanType[planTypeKey];
				const plans = (query.data?.viewer.workspace.plans ?? []).filter(
					(plan) => plan.type === planType,
				);

				const planComponent = <ProductPlan key={planTypeKey} type={planType} plans={plans} />;

				return [planType, planComponent];
			},
		),
	) as { [K in graphql.WorkspacePlanType]: ReactElement };

	const sizeProps: ComponentProps<typeof Box> = { width: '33%', maxWidth: 275 };

	return (
		<Container pt={4} $modal={props.modal}>
			<Box px={4}>
				{props.modal ? (
					<Flex mb={3} alignItems="center">
						<Title flexGrow={1} flexShrink={1}>
							{t('modal.title')}
						</Title>
						<Box ml={2} flexGrow={0} flexShrink={0}>
							<AccountSwitcher />
						</Box>
					</Flex>
				) : (
					<Title mb={3}>{t('title')}</Title>
				)}
				<Paragraph>{t('description')}</Paragraph>
				{query.loading ? (
					<Spinner mt={4} />
				) : (
					<Flex mt={4}>
						<Box mr={4} {...sizeProps}>
							<Subheading>{t('recommendation.heading')}</Subheading>
							<Paragraph mb={4}>{t('recommendation.description')}</Paragraph>
							{planCards.PRO}
						</Box>
						<Box flexGrow={1}>
							<Subheading>{t('alternatives.heading')}</Subheading>
							<Paragraph width={300} mb={4}>
								{t('alternatives.description')}
							</Paragraph>
							<Flex>
								<Box {...sizeProps} mr={3}>
									{planCards.CONVERSATIONS}
								</Box>
								<Box {...sizeProps}>{planCards.ENGAGE}</Box>
							</Flex>
						</Box>
					</Flex>
				)}
			</Box>
		</Container>
	);
}
